// src/lib/utils/format/types.ts
// AUTO-SPLIT from lib/utils/formatters.ts (A10/A2). New code: import from ~/lib/utils/format.

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

/**
 * Configuration options for safe string conversion.
 * 
 * @interface SafeStringOptions
 * @typedef {Object} SafeStringOptions
 * @property {string} [fallback=''] - Default value to return for null/undefined values
 * @property {string} [objectFallback='[Object]'] - Default value for objects that can't be safely stringified
 * @property {string} [arrayFallback='[Array]'] - Default value for arrays that can't be safely stringified
 * @property {string} [functionFallback='[Function]'] - Default value for functions
 * @property {string} [symbolFallback='[Symbol]'] - Default value for symbols
 * @property {boolean} [preserveNumbers=true] - Whether to preserve number formatting
 * @property {boolean} [preserveBooleans=true] - Whether to preserve boolean values as strings
 * @property {boolean} [preserveDates=true] - Whether to format dates using built-in formatters
 * @property {boolean} [tryJSON=false] - Whether to attempt JSON.stringify for objects/arrays
 * @property {number} [maxLength] - Maximum length of the resulting string (will truncate with ellipsis)
 * @property {boolean} [trimWhitespace=true] - Whether to trim leading/trailing whitespace
 * @property {string} [truncateEllipsis='...'] - String to append when truncating
 * @property {NumberFormatOptions} [numberFormat] - Custom number formatting options
 * @property {DateFormatOptions} [dateFormat] - Custom date formatting options
 * @example
 * ```typescript
 * // Basic usage with defaults
 * safeString('Hello, world!') // Returns "Hello, world!"
 * safeString(12345) // Returns "12345"
 * safeString(12345, NumberFormatOption = style: 'currency', currency: 'USD') // Returns "$12,345.00"
 * safeString(true) // Returns "true"
 * safeString(null) // Returns ""
 * safeString(undefined) // Returns ""
 * safeString({ foo: 'bar' }) // Returns "[Object]"
 * safeString([1, 2, 3]) // Returns "[Array]"
 * safeString(Symbol('test')) // Returns "[Symbol]"
 * safeString(new Date()) // Returns ISO date string
 * 
 * // Advanced number formatting
 * safeString(1234.56, { 
 *   numberFormat: { 
 *     style: 'currency', 
 *     currency: 'USD', 
 *     decimals: 2 
 *   } 
 * }) // Returns '$1,234.56'
 * 
 * // Date formatting with multiple styles
 * safeString(new Date(), { 
 *   dateFormat: { 
 *     style: 'short', 
 *     includeTime: true 
 *   } 
 * }) // Returns 'Feb 25, 2024, 3:30 PM'
 * ```
 */
export interface SafeStringOptions {
  /** Default value to return for null/undefined values @default '' */
  fallback?: string
  /** Default value for objects that can't be safely stringified @default '[Object]' */
  objectFallback?: string
  /** Default value for arrays that can't be safely stringified @default '[Array]' */
  arrayFallback?: string
  /** Default value for functions @default '[Function]' */
  functionFallback?: string
  /** Default value for symbols @default '[Symbol]' */
  symbolFallback?: string
  /** Whether to preserve number formatting @default true */
  preserveNumbers?: boolean
  /** Whether to preserve boolean values as strings @default true */
  preserveBooleans?: boolean
  /** Whether to format dates using built-in formatters @default true */
  preserveDates?: boolean
  /** Whether to attempt JSON.stringify for objects/arrays @default false */
  tryJSON?: boolean
  /** Maximum length of the resulting string (will truncate with ellipsis) */
  maxLength?: number
  /** Whether to trim leading/trailing whitespace @default true */
  trimWhitespace?: boolean
  /** String to append when truncating @default '...' */
  truncateEllipsis?: string
  /** Custom number formatting options */
  numberFormat?: NumberFormatOptions
  /** Custom date formatting options */
  dateFormat?: DateFormatOptions
}

/**
 * Number formatting options for various formatting functions.
 * 
 * @interface NumberFormatOptions
 * @property {number} [decimals] - Number of decimal places to show
 * @property {boolean} [useGrouping=true] - Whether to use thousands separators (e.g., 1,000)
 * @property {'decimal' | 'currency' | 'percent' | 'compact'} [style='decimal'] - Format style
 * @property {string} [currency='USD'] - Currency code for currency formatting
 * @property {string} [locale='en-US'] - Locale for number formatting
 * @property {number} [minimumFractionDigits] - Minimum number of decimal places
 * @property {number} [maximumFractionDigits] - Maximum number of decimal places
 */
export interface NumberFormatOptions {
  /** Number of decimal places to show (0-20) */
  decimals?: number
  /** Whether to use thousands separators @default true */
  useGrouping?: boolean
  /** Format style @default 'decimal' */
  style?: 'decimal' | 'currency' | 'percent' | 'compact'
  /** Currency code for currency formatting @default 'USD' */
  currency?: string
  /** Locale for number formatting @default 'en-US' */
  locale?: string
  /** Minimum number of decimal places */
  minimumFractionDigits?: number
  /** Maximum number of decimal places */
  maximumFractionDigits?: number
}

/**
 * Currency formatting options for formatCurrency function.
 * 
 * @interface CurrencyOptions
 * @property {number} [decimals] - Number of decimal places to show
 * @property {boolean} [showSymbol=true] - Whether to show the currency symbol
 * @property {string} [currency='USD'] - Currency code for formatting
 * @property {string} [locale='en-US'] - Locale for formatting
 */
export interface CurrencyOptions {
  /** Number of decimal places to show (0-20) */
  decimals?: number
  /** Whether to show the currency symbol @default true */
  showSymbol?: boolean
  /** Currency code for formatting @default 'USD' */
  currency?: string
  /** Locale for formatting @default 'en-US' */
  locale?: string
}

/**
 * Date formatting options for safe string conversion.
 * 
 * @interface DateFormatOptions
 * @property {'full' | 'monthDay' | 'shortMonthDay' | 'shortMonthYear' | 'monthYear' | 'short' | 'iso' | 'datetime' | 'relative' | 'custom'} [style='short'] - Format style
 * @property {string} [locale='en-US'] - Locale for date formatting
 * @property {string} [timeZone] - Timezone for date formatting
 * @property {boolean} [includeTime=false] - Whether to include time in the output
 * @property {string} [customFormat] - Custom date format string (for style: 'custom')
 * @property {RelativeTimeOptions} [relativeOptions] - Options for relative time formatting
 * @example
 * ```typescript
 * // Basic usage with defaults
 * formatDate('2024-02-25') // Returns "February 25, 2024"
 * formatDate('2024-02-25', style='short') // Returns "2/25/2024"
 * formatDate('2024-02-25', style='monthDay') // Returns "February 25"
 * formatDate('2024-02-25', style='relative') // Returns "2 days ago"
 * formatDate('2024-02-25', style='iso') // Returns "2024-02-25"
 * formatDate('2024-02-25', style='datetime') // Returns "2024-02-25T00:00:00.000Z"
 * formatDate('2024-02-25', includeTime=true) // Returns "February 25, 2024 12:00 AM"
 * 
 * // Example configuration options with custom format
 * const options: DateFormatOptions = {
 *   style: 'full',
 *   locale: 'en-US',
 *   timeZone: 'America/New_York',
 *   includeTime: true,
 *   customFormat: 'MM/dd/yyyy hh:mm a'
 * }
 * const options: DateFormatOptions = {
 *   style: 'relative',
 *   locale: 'en-US',
 *   relativeOptions: { unit: 'auto', numeric: 'auto' }
 * }
 * formatDate(new Date(), options) // Returns 'today' or '2 days ago'
 * ```
 */
export interface DateFormatOptions {
  /** Format style @default 'short' */
  style?: 'full' | 'monthDay' | 'shortMonthDay' | 'shortMonthYear' | 'monthYear' | 'short' | 'iso' | 'datetime' | 'relative' | 'custom'
  /** Locale for date formatting @default 'en-US' */
  locale?: string
  /** Timezone for date formatting */
  timeZone?: string
  /** Whether to include time in the output @default false */
  includeTime?: boolean
  /** Custom date format string (for style: 'custom') */
  customFormat?: string
  /** Options for relative time formatting */
  relativeOptions?: RelativeTimeOptions
}

/**
 * Options for relative time formatting.
 * 
 * @interface RelativeTimeOptions
 * @property {'auto' | 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'} [unit='auto'] - Unit for relative formatting
 * @property {'always' | 'auto'} [numeric='auto'] - Numeric style
 */
export interface RelativeTimeOptions {
  /** Unit for relative formatting @default 'auto' */
  unit?: 'auto' | 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year'
  /** Numeric style @default 'auto' */
  numeric?: 'always' | 'auto'
}

/**
 * Defines capitalization strategies for text formatting.
 * 
 * @enum {string} CapitalizationStrategy
 * @property {string} TITLE_CASE - Capitalize the first letter of each word (with minor word exceptions)
 * @property {string} ALL_CAPS - Convert all characters to uppercase
 * @property {string} ALL_LOWERCASE - Convert all characters to lowercase
 * @property {string} SENTENCE_CASE - Capitalize only the first letter of the first word
 * @property {string} CAMEL_CASE - Convert to camelCase (first word lowercase, subsequent words capitalized)
 * @property {string} PASCAL_CASE - Convert to PascalCase (all words capitalized, no spaces)
 * @property {string} SNAKE_CASE - Convert to snake_case (lowercase with underscores)
 * @property {string} KEBAB_CASE - Convert to kebab-case (lowercase with hyphens)
 * @property {string} CONSTANT_CASE - Convert to CONSTANT_CASE (uppercase with underscores)
 * @property {string} DOT_CASE - Convert to dot.case (lowercase with dots)
 * @property {string} PRESERVE - Keep the original capitalization unchanged
 * 
 * @example
 * ```typescript
 * // Title case with smart minor word handling
 * formatText("the quick brown fox", { capitalization: CapitalizationStrategy.TITLE_CASE })
 * // Returns "The Quick Brown Fox"
 * 
 * // Sentence case
 * formatText("the quick brown fox", { capitalization: CapitalizationStrategy.SENTENCE_CASE })
 * // Returns "The quick brown fox"
 * 
 * // Programming case styles
 * formatText("the quick brown fox", { capitalization: CapitalizationStrategy.CAMEL_CASE })
 * // Returns "theQuickBrownFox"
 * 
 * formatText("the quick brown fox", { capitalization: CapitalizationStrategy.SNAKE_CASE })
 * // Returns "the_quick_brown_fox"
 * 
 * formatText("the quick brown fox", { capitalization: CapitalizationStrategy.KEBAB_CASE })
 * // Returns "the-quick-brown-fox"
 * ```
 */
export enum CapitalizationStrategy {
  /** Capitalize the first letter of each word (with minor word exceptions) */
  TITLE_CASE = 'titleCase',
  /** Convert all characters to uppercase */
  ALL_CAPS = 'allCaps',
  /** Convert all characters to lowercase */
  ALL_LOWERCASE = 'allLowercase',
  /** Capitalize only the first letter of the first word */
  SENTENCE_CASE = 'sentenceCase',
  /** Convert to camelCase (first word lowercase, subsequent words capitalized) */
  CAMEL_CASE = 'camelCase',
  /** Convert to PascalCase (all words capitalized, no spaces) */
  PASCAL_CASE = 'pascalCase',
  /** Convert to snake_case (lowercase with underscores) */
  SNAKE_CASE = 'snakeCase',
  /** Convert to kebab-case (lowercase with hyphens) */
  KEBAB_CASE = 'kebabCase',
  /** Convert to CONSTANT_CASE (uppercase with underscores) */
  CONSTANT_CASE = 'constantCase',
  /** Convert to dot.case (lowercase with dots) */
  DOT_CASE = 'dotCase',
  /** Keep the original capitalization unchanged */
  PRESERVE = 'preserve'
}

/**
 * Configuration options for text formatting.
 * 
 * @interface FormattingOptions
 * @property {CapitalizationStrategy} [capitalization=CapitalizationStrategy.TITLE_CASE] - The capitalization strategy to apply
 * @property {Record<string, string>} [specialCases] - Special cases that override the default capitalization
 * @property {string[]} [minorWords] - Words that should remain lowercase in title case (unless they're the first or last word)
 * @property {boolean} [preserveInternalCaps=true] - Whether to preserve the case of words with internal capitalization (e.g., "iPhone")
 * @property {string} [wordSeparator=' '] - Character used to join words when formatting for display
 * @property {string[]} [wordDelimiters] - Characters that should be treated as word delimiters when splitting the input string
 * @property {boolean} [mergeSpecialCases=true] - Whether to merge provided special cases with default ones
 * @property {boolean} [mergeHyphenatedWords=true] - Whether to merge provided hyphenated words with default ones
 * @property {string[]} [hyphenatedWords] - Custom hyphenated words to consider
 * @property {boolean} [isFirstOrLastWord=false] - Whether the word is the first or last in the phrase
 * @property {boolean} [trimWhitespace=true] - Whether to trim leading/trailing whitespace
 * @property {number} [maxLength] - Maximum length of output (will truncate with ellipsis)
 * @property {string} [truncateEllipsis='...'] - String to append when truncating
 * @property {boolean} [removeExtraSpaces=true] - Whether to collapse multiple spaces into single spaces
 */
export interface FormattingOptions {
  /** The capitalization strategy to apply @default CapitalizationStrategy.TITLE_CASE */
  capitalization?: CapitalizationStrategy
  specialCases?: Record<string, string>
  hyphenatedWords?: string[]
  /** Whether to merge provided special cases with default ones @default true */
  mergeSpecialCases?: boolean
  /** Whether to merge provided hyphenated words with default ones @default true */
  mergeHyphenatedWords?: boolean
  minorWords?: string[]
  /** Whether to preserve the case of words with internal capitalization @default true */
  preserveInternalCaps?: boolean
  /** Character used to join words when formatting for display @default ' ' */
  wordSeparator?: string
  wordDelimiters?: string[]
  /** Whether the word is the first or last in the phrase @default false */
  isFirstOrLastWord?: boolean
  /** Whether to trim leading/trailing whitespace @default true */
  trimWhitespace?: boolean
  maxLength?: number
  /** String to append when truncating @default '...' */
  truncateEllipsis?: string
  /** Whether to collapse multiple spaces into single spaces @default true */
  removeExtraSpaces?: boolean
}

/**
 * Helper interface for title case formatting options.
 * 
 * @interface TitleCaseOptions
 * @typedef {Object} TitleCaseOptions
 * @property {string[]} [minorWords=DEFAULT_MINOR_WORDS] - Words that should remain lowercase (unless they're the first or last word)
 * @property {boolean} [preserveInternalCaps=true] - Whether to preserve the case of words with internal capitalization
 * @property {boolean} [isFirstOrLastWord=false] - Whether the word is the first or last in the phrase
 * @property {Record<string, string>} [specialCases=DEFAULT_SPECIAL_CASES] - Special cases that override the default capitalization
 * @property {string[]} [hyphenatedWords=HYPHENATED_WORDS] - Hyphenated words to preserve
 * @property {boolean} [mergeSpecialCases=true] - Whether to merge provided special cases with default ones
 * @property {boolean} [mergeHyphenatedWords=true] - Whether to merge provided hyphenated words with default ones
 */
export interface TitleCaseOptions {
  minorWords?: string[]
  preserveInternalCaps?: boolean
  isFirstOrLastWord?: boolean
  specialCases?: Record<string, string>
  hyphenatedWords?: string[]
  mergeSpecialCases?: boolean
  mergeHyphenatedWords?: boolean
}
